import React, { FC, useCallback, useMemo } from "react";
import { connect } from "react-redux";

import { faAngleDown, faBriefcase, faLink, faUpload } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Dropdown from "antd/es/dropdown";
import { defineMessages, useIntl } from "react-intl";
import { useParams } from "react-router-dom";

import Folder from "@mapmycustomers/shared/types/entity/Folder";
import User from "@mapmycustomers/shared/types/User";
import { Button } from "@mapmycustomers/ui";

import { getCurrentUserId } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import useAnalytics from "@app/util/contexts/useAnalytics";

import ResourceType from "../enum/ResourceType";
import { getFolders } from "../store";

import styles from "./Header.module.scss";

const messages = defineMessages({
  add: {
    id: "resources.header.resource.add",
    defaultMessage: "Add Resource",
    description: "Add resource button title",
  },
  addLink: {
    id: "resources.header.addLink",
    defaultMessage: "Add Link",
    description: "Add link menu item title",
  },
  title: {
    id: "resources.header.title",
    defaultMessage: "Resources",
    description: "Resources page header title",
  },
  upload: {
    id: "resources.header.upload",
    defaultMessage: "Upload File",
    description: "Upload file menu item title",
  },
});

export interface HeaderProps {
  currentUserId: User["id"];
  folders: Folder[];
  onAddResourceClick: (type: ResourceType) => void;
}

const Header: FC<HeaderProps> = ({ currentUserId, folders, onAddResourceClick }) => {
  const analytics = useAnalytics();
  const intl = useIntl();
  const { folderId } = useParams<{ folderId?: string }>();
  const activeFolderId = folderId ? parseInt(folderId) : undefined;

  const allowAdd = useMemo(() => {
    const folder = folders.find(({ id }) => id === activeFolderId);
    return !folder || (folder.userId === currentUserId && !folder.isTrash);
  }, [activeFolderId, currentUserId, folders]);

  const items = useMemo(
    () => [
      {
        icon: <FontAwesomeIcon icon={faUpload} />,
        key: "uploadFile",
        label: intl.formatMessage(messages.upload),
        onClick: () => {
          analytics.initiated(["Add Resource", "Upload File"]);
          onAddResourceClick(ResourceType.FILE);
        },
      },
      {
        icon: <FontAwesomeIcon icon={faLink} />,
        key: "addLink",
        label: intl.formatMessage(messages.addLink),
        onClick: () => {
          analytics.initiated(["Add Resource", "Add Link"]);
          onAddResourceClick(ResourceType.LINK);
        },
      },
    ],
    [analytics, intl, onAddResourceClick]
  );

  const handleClick = useCallback(() => {
    analytics.clicked(["Add Resource"]);
  }, [analytics]);

  return (
    <div className={styles.container}>
      <div className={styles.left}>
        <FontAwesomeIcon icon={faBriefcase} />
        <span>{intl.formatMessage(messages.title)}</span>
      </div>
      {allowAdd && (
        <Dropdown menu={{ items }} trigger={["click"]}>
          <Button
            onClick={handleClick}
            rightIcon={<FontAwesomeIcon icon={faAngleDown} />}
            type="primary"
          >
            {intl.formatMessage(messages.add)}
          </Button>
        </Dropdown>
      )}
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  currentUserId: getCurrentUserId(state)!,
  folders: getFolders(state),
});

export default connect(mapStateToProps)(Header);
